// Third-party
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DropEvent, FileError as DropzoneFileError, ErrorCode, FileRejection } from 'react-dropzone';
import { connect } from 'react-redux';
import Papa from 'papaparse';
import { isNil } from 'lodash';

// Components
import UploadStep, { AvailableTemplateDownloads } from './steps/UploadStep';
import ProcessingStep from './steps/ProcessingStep';
import ImportStep from './steps/ImportStep';
import ErrorStep from './steps/ErrorStep';
import DefineHeadersStep from './steps/DefineHeadersStep';
import FileUploaderActions from './FileUploaderActions';
import FileUploaderOverwiteModal from '../../FileUploaderOverwriteWarningModal';

// Utils
import notification from '@/utils/notification';
import { canAccessTaxAnalysis, canAccessUploadCSV } from '@/utils/feature-access';
import { detectTaxTemplate, getTemplateConfigForRegion, normalizeHeaderToKey, validateHeadersMatch } from './utils/csv-template-helpers';
import { formatBytes, formatMessage } from '@/utils/i18n';
import { sanitizeDecimalValue } from '@/utils/portfolio/sanitize';

// Actions
import { startLoaderAction, stopLoaderAction } from '@/redux/modules/ui/loaders';
import { selectShelfStatusedPortfolioHoldings } from '@/redux/modules/shelf-management/selectors';
import { addHoldingsToPortfolioFromCsv, removeAllHoldingsFromPortfolio, setShowTaxDetails } from '@/redux/modules/edit-portfolio';
import { togglePortfolioEditor } from '@/redux/modules/common/actionCreators';
import { getContactById } from '@/redux/modules/contacts';

// Types
import Comparison from '@/types/comparison';
import Dictionary from '@/types/dictionary';
import GenericContact from '@/types/generic-contact';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import { loadNewPortfolio } from '@/redux/modules/comparison';
import { ReduxState } from '@/redux/modules/types';
import { getUserAppRegion } from '@/utils/users';
import { isUserInLotRegion } from '@/utils/tax-analysis';

type StateProps = {
  hasPortfolioHoldings: boolean;
  comparison: Comparison | undefined;
  portfolioIsModel?: boolean;
  user?: Nullable<User>;
};

type DispatchProps = {
  startLoaderAction: (name: string, params?: Dictionary<unknown>) => void;
  stopLoaderAction: (name: string) => void;
  removeAllHoldingsFromPortfolio: () => void;
  addHoldingsToPortfolioFromCsv: (data: any) => void;
  togglePortfolioEditor: (isVisible: boolean, isPinned?: boolean, isFocused?: boolean, isReadOnly?: boolean) => void;
  loadNewPortfolio: (contact?: GenericContact, createItem?: boolean) => Promise<void>;
  getContactById: (id: string) => Promise<GenericContact>;
  setShowTaxDetails: (show: boolean) => void;
};

type Props = StateProps &
  DispatchProps & {
    initialStep?: FileUploaderSteps;
    onClose: () => void;
  };

export enum FileUploaderSteps {
  UPLOAD,
  ERROR,
  PROCESSING,
  IMPORT,
  DEFINE_HEADERS,
}

type DropHandlerParams = {
  acceptedFiles: Array<File>;
  fileRejections: Array<FileRejection>;
  event: DropEvent;
};

export type FileUploadError = Partial<DropzoneFileError> & {
  param?: string;
};

export type CsvFormat = {
  exchange?: string;
  identifier: string;
  units: number;
  purchaseDate?: Date;
  costBasis?: number;
};

const FileUploaderFlow: React.FC<Props> = ({
  initialStep = FileUploaderSteps.UPLOAD,
  onClose,
  startLoaderAction,
  stopLoaderAction,
  hasPortfolioHoldings,
  removeAllHoldingsFromPortfolio,
  addHoldingsToPortfolioFromCsv,
  togglePortfolioEditor,
  loadNewPortfolio,
  comparison,
  getContactById,
  portfolioIsModel,
  setShowTaxDetails,
  user,
}) => {
  const [currentStep, setCurrentStep] = useState<FileUploaderSteps>(initialStep);
  const [dropHandlerParams, setDropHandlerParams] = useState<DropHandlerParams>();
  const [showOverwriteWarningModal, setShowOverwriteWarningModal] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [file, setFile] = useState<File>();
  const [errors, setErrors] = useState<Array<FileUploadError>>([]);
  const [payload, setPayload] = useState<{
    instruments: Array<CsvFormat>;
    lotsEnabled?: boolean;
  }>();
  const intervalRef = useRef<number>();
  const [headers, setHeaders] = useState<Array<string>>([]);
  const [contact, setContact] = useState<GenericContact | undefined>(undefined);
  const isTaxAnalysisFeatureEnabled = canAccessTaxAnalysis(user) && !portfolioIsModel;
  const appRegion = getUserAppRegion(user);

  const { templateHeaders, validHeaderSet } = getTemplateConfigForRegion(appRegion, isTaxAnalysisFeatureEnabled);
  const maxSizeInBytes = 5_000_000;
  const maxRows = 400;

  const isTaxTemplate = useMemo(
    () => detectTaxTemplate(headers, templateHeaders, appRegion, isTaxAnalysisFeatureEnabled),
    [headers, templateHeaders, appRegion, isTaxAnalysisFeatureEnabled],
  );

  const accept = {
    'text/csv': ['.csv'],
  };

  useEffect(() => {
    async function getContact() {
      if (comparison && comparison.contact && comparison.contact._id) {
        const retrievedContact = await getContactById(comparison.contact._id);
        setContact(retrievedContact);
      }
    }
    getContact();
  }, [comparison, getContactById]);

  const handleReset = (): void => {
    setCurrentStep(initialStep);
    setProgress(0);
    setPayload(undefined);
    setErrors([]);
    setFile(undefined);
    clearInterval(intervalRef.current);
    stopLoaderAction('PORTFOLIO_FILE_UPLOAD');
  };

  const handleClose = (): void => {
    handleReset();
    onClose();
  };

  const handleDrop = (acceptedFiles: Array<File>, fileRejections: Array<FileRejection>, event: DropEvent): void => {
    // Display warning modal if there are holdings in the current portfolio
    if (hasPortfolioHoldings) {
      setDropHandlerParams({ acceptedFiles, fileRejections, event });
      setShowOverwriteWarningModal(true);
    } else {
      handleUpload(acceptedFiles, fileRejections);
    }
  };

  const handleDownload = (isTaxTemplate = false) => {
    const headerKey = isTaxTemplate ? 'Modal.PortfolioUpload.CSVTemplate.TaxHeaders' : 'Modal.PortfolioUpload.CSVTemplate.Headers';
    const fileNameKey = isTaxTemplate ? 'Modal.PortfolioUpload.CSVTemplate.TaxFileName' : 'Modal.PortfolioUpload.CSVTemplate.FileName';
    const csvData = `${formatMessage(headerKey)}\n`;
    // UTF-8 Byte order mark so Excel opens the CSV in the correct encoding
    const blob = new Blob([new Uint8Array([0xef, 0xbb, 0xbf]), csvData], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = formatMessage(fileNameKey);
    anchor.click();

    URL.revokeObjectURL(url);
    anchor.remove();
  };

  const validateDataToImport = async (data: CsvFormat[]) => {
    const payload = formatData(data);

    if (!validateData(data)) {
      return;
    }

    // Valid file
    simulateProgress(file?.size ?? 1000);
    setPayload(payload);
    stopLoaderAction('PORTFOLIO_FILE_UPLOAD');
    setCurrentStep(FileUploaderSteps.IMPORT);
  };

  const handleUpload = async (acceptedFiles: Array<File>, fileRejections: Array<FileRejection>, reorderColumns = false): Promise<void> => {
    setProgress(0);
    startLoaderAction('PORTFOLIO_FILE_UPLOAD');
    setCurrentStep(FileUploaderSteps.PROCESSING);

    // Validate file type and file size
    if (!validateFile(fileRejections)) {
      return;
    }

    setFile(acceptedFiles[0]);

    const parsedCsv = await parseCsv(acceptedFiles[0]);

    // We need to set headers after parsing so downstream logic (like tax template detection) has access to the uploaded column names
    setHeaders(parsedCsv.meta.fields ?? []);

    if (!validateStructure(parsedCsv)) {
      return;
    }

    if (reorderColumns) {
      const normalizedData = normalizeReorderedHeaders(parsedCsv);
      return validateDataToImport(normalizedData);
    }

    // Validate headers
    if (!validateHeaders(parsedCsv)) {
      return;
    }

    const normalizedData = normalizeHeaders(parsedCsv);
    await validateDataToImport(normalizedData);
  };

  const parseCsv = (file: File): Promise<Papa.ParseResult<CsvFormat>> =>
    new Promise((resolve, reject) => {
      Papa.parse<CsvFormat, File>(file, {
        header: true,
        skipEmptyLines: true,
        worker: true,
        encoding: 'utf-8',
        complete: results => {
          resolve(results);
        },
        error: error => {
          // This is only for FileReader API errors
          reject(error);
        },
      });
    });

  const validateHeaders = (parsedCsv: Papa.ParseResult<CsvFormat>): boolean => {
    const { fields } = parsedCsv.meta;
    if (!fields || fields.length === 0) {
      setErrors([
        {
          code: 'no-headers',
        },
      ]);
      setCurrentStep(FileUploaderSteps.ERROR);
      stopLoaderAction('PORTFOLIO_FILE_UPLOAD');
      return false;
    }

    const matchingHeaders = validateHeadersMatch(fields, validHeaderSet);

    if (!matchingHeaders) {
      setHeaders(fields);
      setCurrentStep(FileUploaderSteps.DEFINE_HEADERS);
      return false;
    }

    return true;
  };

  // Validates checks for file type and file size
  const validateFile = (fileRejections: Array<FileRejection>): boolean => {
    if (fileRejections.length === 0) {
      return true;
    }

    setErrors(
      fileRejections[0].errors.map<FileUploadError>(error => {
        if (error.code === ErrorCode.FileTooLarge) {
          return {
            ...error,
            param: formatBytes(maxSizeInBytes),
          };
        }
        if (error.code === ErrorCode.FileTooSmall) {
          return {
            ...error,
          };
        }

        return error;
      }),
    );
    setCurrentStep(FileUploaderSteps.ERROR);
    stopLoaderAction('PORTFOLIO_FILE_UPLOAD');

    return false;
  };

  const validateStructure = (parsedCsv: Papa.ParseResult<CsvFormat>): boolean => {
    switch (true) {
      case parsedCsv.errors.length > 0:
        setErrors([
          {
            code: 'unreadable-file',
          },
        ]);
        setCurrentStep(FileUploaderSteps.ERROR);
        stopLoaderAction('PORTFOLIO_FILE_UPLOAD');
        return false;

      case !parsedCsv.data.length:
        setErrors([{ code: 'no-data' }]);
        setCurrentStep(FileUploaderSteps.ERROR);
        stopLoaderAction('PORTFOLIO_FILE_UPLOAD');
        return false;

      case parsedCsv.data.length > maxRows:
        setErrors([{ code: 'too-many-rows', param: `${maxRows}` }]);
        setCurrentStep(FileUploaderSteps.ERROR);
        stopLoaderAction('PORTFOLIO_FILE_UPLOAD');
        return false;

      default:
        return true;
    }
  };

  // Normalizes the data by transforming french headers into english and removing format hints
  // Only includes keys that are valid for the current region (e.g., excludes purchaseDate in CAN)
  const normalizeHeaders = (parseCsv: Papa.ParseResult<any>): Array<CsvFormat> =>
    parseCsv.data.map(row => {
      const data: any = {};

      Object.keys(row).forEach(key => {
        const headerKey = normalizeHeaderToKey(key);
        if (headerKey && validHeaderSet.has(headerKey)) {
          data[headerKey] = row[key];
        }
      });

      return data as CsvFormat;
    });

  // Only map columns up to the template length - extra CSV columns are ignored
  const normalizeReorderedHeaders = (parseCsv: Papa.ParseResult<any>): Array<CsvFormat> =>
    parseCsv.data.map(row => {
      const data: any = {};

      const columnsToMap = headers.slice(0, templateHeaders.length);
      columnsToMap.forEach((header, index) => {
        const templateHeader = templateHeaders[index];
        const normalizedKey = normalizeHeaderToKey(templateHeader);

        if (normalizedKey) {
          data[normalizedKey] = row[header];
        }
      });

      return data as CsvFormat;
    });

  const validatePositiveNumber = (value: number, index: number) => {
    const errors: Array<FileUploadError> = [];
    if (Number.isNaN(value)) {
      errors.push({
        code: 'units-not-a-number',
        param: index.toString(),
      });
    }

    if (value <= 0) {
      errors.push({
        code: 'units-negative',
        param: index.toString(),
      });
    }

    return errors;
  };
  const validateData = (data: Array<CsvFormat>): boolean => {
    const errors: Array<FileUploadError> = [];

    data.forEach(({ identifier, units, costBasis }, index) => {
      // Skip 0 and header row
      index += 2;

      if (!identifier) {
        errors.push({
          code: 'identifier-empty',
          param: index.toString(),
        });
      }

      if (!units) {
        errors.push({
          code: 'units-empty',
          param: index.toString(),
        });
      }

      errors.push(...validatePositiveNumber(units, index));

      if (costBasis) {
        errors.push(...validatePositiveNumber(costBasis, index));
      }
    });

    if (errors.length > 0) {
      setErrors(errors);
      setCurrentStep(FileUploaderSteps.ERROR);
      stopLoaderAction('PORTFOLIO_FILE_UPLOAD');
      return false;
    }

    return true;
  };

  // Removes empty values and wraps in object. Useful for `exchange` because back-end expect the property to not be defined and not just empty.
  const formatData = (data: Array<CsvFormat>) => {
    const lotsEnabled = isTaxAnalysisFeatureEnabled && isUserInLotRegion(user) && data.some(row => !isNil(row.costBasis) || !isNil(row.purchaseDate));

    return {
      instruments: data.map((item: Record<string, any>) => {
        for (const key in item) {
          if (item.hasOwnProperty(key)) {
            if (item[key] === null || item[key] === undefined || item[key] === '') {
              delete item[key];
            }
          }
        }

        // Format number from string type to number type
        item.units = sanitizeDecimalValue(item.units);

        if (!isNil(item.costBasis)) {
          item.costBasis = sanitizeDecimalValue(item.costBasis);
        }

        return item;
      }) as Array<CsvFormat>,
      ...(lotsEnabled && { lotsEnabled }),
    };
  };

  const handleImport = (): void => {
    if (!payload) {
      return;
    }

    if (isTaxAnalysisFeatureEnabled && isTaxTemplate) {
      setShowTaxDetails(true);
    }

    loadNewPortfolio(contact);
    // Handle sending payload to back-end endpoint
    try {
      addHoldingsToPortfolioFromCsv(payload);
    } catch (e) {
      notification.error(formatMessage('Modal.PortfolioUpload.Error'));
    }

    onClose();
    togglePortfolioEditor(true);
    notification.success(formatMessage('Modal.PortfolioUpload.SuccessfullyUploadedToast'));
  };

  const handleConfirmOverwrite = (): void => {
    if (dropHandlerParams) {
      const { acceptedFiles, fileRejections } = dropHandlerParams;
      setShowOverwriteWarningModal(false);
      setDropHandlerParams(undefined);
      removeAllHoldingsFromPortfolio();
      handleUpload(acceptedFiles, fileRejections);
    }
  };

  // This simulates loading time
  const simulateProgress = (fileSize: number): Promise<void> =>
    new Promise<void>(resolve => {
      intervalRef.current = window.setInterval(() => {
        setProgress(prevProgress => {
          if (prevProgress < 100) {
            const factor = (1 / fileSize) * 100_000;

            return Math.min(prevProgress + Math.floor(Math.random() * 100) * factor, 100);
          }
          clearInterval(intervalRef.current);

          // Wait for transition to finish

          setTimeout(resolve, 1000);

          return prevProgress;
        });
      }, 50);
    });

  const getAvailableTemplateDownloads = (): AvailableTemplateDownloads => {
    const hasTaxAccess = canAccessTaxAnalysis(user);
    const hasUploadCSVAccess = canAccessUploadCSV(user);

    if (hasTaxAccess && hasUploadCSVAccess) {
      return 'standard_and_tax';
    }

    if (hasTaxAccess) {
      return 'tax';
    }

    return 'standard';
  };

  const renderStep = (): React.ReactNode => {
    switch (currentStep) {
      case FileUploaderSteps.UPLOAD:
        return (
          <UploadStep
            maxSize={maxSizeInBytes}
            accept={accept}
            onDrop={handleDrop}
            onDownloadTemplate={handleDownload}
            availableTemplateDownloads={getAvailableTemplateDownloads()}
          />
        );
      case FileUploaderSteps.PROCESSING:
        return <ProcessingStep progress={progress} file={file} />;
      case FileUploaderSteps.IMPORT:
        return <ImportStep file={file} />;
      case FileUploaderSteps.ERROR:
        return <ErrorStep errors={errors} />;
      case FileUploaderSteps.DEFINE_HEADERS:
        return (
          <DefineHeadersStep
            file={file}
            headers={headers}
            templateHeaders={templateHeaders}
            updateHeadersOrder={updatedHeaders => setHeaders(updatedHeaders)}
          />
        );
      default:
        // Throws a TS error if not all steps are tested for in switch statement
        const exhaustiveCheck: never = currentStep;
        throw new Error(exhaustiveCheck);
    }
  };

  const handleContinueToImport = () => {
    if (file) handleUpload([file], [], true);
  };

  return (
    <>
      <div className="file-uploader-panel">
        {renderStep()}
        <FileUploaderActions
          onCancel={handleClose}
          currentStep={currentStep}
          onImport={handleImport}
          onUpload={handleReset}
          onContinue={handleContinueToImport}
        />
      </div>

      <FileUploaderOverwiteModal show={showOverwriteWarningModal} onCancel={() => setShowOverwriteWarningModal(false)} onConfirm={handleConfirmOverwrite} />
    </>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  comparison: state.comparison.activeComparison,
  hasPortfolioHoldings: selectShelfStatusedPortfolioHoldings(state.editPortfolio.portfolio).holdings.length > 0,
  portfolioIsModel: state.editPortfolio.isCreatingModel || false,
  user: state.auth.user,
});

export default connect(mapStateToProps, {
  startLoaderAction,
  stopLoaderAction,
  removeAllHoldingsFromPortfolio,
  addHoldingsToPortfolioFromCsv,
  togglePortfolioEditor,
  loadNewPortfolio,
  getContactById,
  setShowTaxDetails,
})(FileUploaderFlow);
